import { Avatar, Badge } from '@roger/ui-components';
import { HeartPulse, Megaphone, Users } from 'lucide-react';
import type { InboxRow as InboxRowData } from '../../services/api';
import { fitName } from './fitText';
import { FittedText } from './FittedText';
import { initialsOf } from './initials';
import { MentionBadge } from './MentionBadge';
import { UnreadPill } from './UnreadPill';

interface InboxRowProps {
  row: InboxRowData;
  onOpen: (channelId: string) => void;
  /**
   * The care-team thread for the chart on screen. Highlighted with
   * the tokens the sidebar already uses for its active item, so no new design
   * system variant is needed.
   */
  highlighted?: boolean;
  /** `compact` is the dock row; `default` keeps the 40px avatar for the sheet. */
  density?: 'default' | 'compact';
  /** The conversation open in a window: a muted fill. */
  active?: boolean;
}

function timeLabel(iso: string | null | undefined): string {
  if (!iso) return '';
  const when = new Date(iso);
  const today = new Date();
  const sameDay = when.toDateString() === today.toDateString();
  return sameDay
    ? when.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    : when.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function InboxRow({
  row,
  onOpen,
  highlighted = false,
  density = 'default',
  active = false,
}: InboxRowProps) {
  const compact = density === 'compact';
  const disc = compact ? 'h-[34px] w-[34px]' : 'h-10 w-10';
  const unread = row.unread_count ?? 0;
  // The server's marker, not the member count: a shared group or care team can
  // have one or two members and still gets the group icon.
  const isDirect = row.is_direct;
  const patient = row.patient;

  return (
    <button
      type='button'
      data-chat-row={row.channel_id}
      onClick={() => onOpen(row.channel_id)}
      aria-current={highlighted ? 'true' : undefined}
      data-active={active ? 'true' : undefined}
      // The 10px `pl-2.5` plus the 2px rule keep the pinned row's content at the
      // same 12px inset that `px-3` gives every other row, so the avatar stays in
      // line.
      className={`flex w-full items-start gap-3 text-left focus:bg-muted ${
        compact ? 'px-3.5 py-[9px]' : 'min-h-16 px-3 py-2'
      } ${
        highlighted
          ? 'border-l-2 border-primary pl-2.5 bg-accent/50 hover:bg-accent/30'
          : active
            ? 'bg-muted'
            : 'hover:bg-muted'
      }`}
    >
      {row.kind === 'announcement' ? (
        <span
          className={`flex ${disc} shrink-0 items-center justify-center rounded-full ${
            row.is_member === false
              ? 'bg-muted text-muted-foreground'
              : 'bg-primary-50 text-primary-600'
          }`}
        >
          <Megaphone className='h-4 w-4' />
        </span>
      ) : isDirect ? (
        <Avatar
          size='md'
          initials={initialsOf(row.title)}
          className={`shrink-0 ${disc}`}
        />
      ) : (
        <span
          className={`flex ${disc} shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-600`}
        >
          {patient ? (
            <HeartPulse className='h-4 w-4' />
          ) : (
            <Users className='h-4 w-4' />
          )}
        </span>
      )}

      <span className='min-w-0 flex-1'>
        <span className='flex items-baseline justify-between gap-2'>
          {patient ? (
            <span className='flex min-w-0 items-center gap-2'>
              <FittedText
                text={row.title}
                fit={fits =>
                  fitName(patient.first_name, patient.last_name, fits)
                }
                className={`${compact ? 'text-[13px]' : 'text-sm'} ${unread > 0 ? 'font-bold' : 'font-medium'}`}
              />
              <Badge
                variant='outline'
                className='font-medium text-muted-foreground'
              >
                Patient
              </Badge>
            </span>
          ) : (
            <span
              className={`truncate ${compact ? 'text-[13px]' : 'text-sm'} ${unread > 0 ? 'font-bold' : 'font-medium'}`}
            >
              {row.title}
            </span>
          )}
          <span className='shrink-0 text-[11px] text-muted-foreground'>
            {timeLabel(row.last_message?.created_at)}
          </span>
        </span>
        <span
          className={`block truncate text-xs ${unread > 0 ? 'text-foreground' : 'text-muted-foreground'}`}
        >
          {row.last_message
            ? `${row.last_message.sender_name}: ${row.last_message.preview}`
            : ''}
        </span>
      </span>

      {row.has_unread_mention && <MentionBadge className='ml-1' />}
      <UnreadPill count={unread} className='ml-1' />
    </button>
  );
}
